import { useEffect, useMemo, useRef, useState } from 'react';
import { breakpoints, Column, Columns, Heading, Inline, Stack, Text, vars } from '@etg/wings';
import { CarrierLogo, ContentWrapper, LoadingDots, Notification, variants } from '@eti/components';
import { css, cx } from '@eti/styles';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { Calendar, Prohibit } from '@phosphor-icons/react';
import { CancellationRefundOfferReason, PartOfExternalFlights } from '@eti/schema-types';
import { useMutation } from '@apollo/client';
import { differenceInSeconds, isAfter } from 'date-fns';
import { useMediaQuery } from 'usehooks-ts';
import TitledCard from '../../../../common/components/TitledCard';
import HotelsAndCars from '../overview/HotelsAndCars';
import { REBOOK_ELIGIBILITY } from '../../containers/queries';
import { screenReadersOnlyStyles } from '../../../../utils/styleUtils';
import OrderDetailsUpgradeTripBanner from '../common/OrderDetailsUpgradeTripBanner';
import FareRulesDrawerButton from '../common/FareRulesDrawerButton';
import RyanairFlightInfoBox from '../common/RyanairFlightInfoBox';
import { useOrderDetails } from '../../containers/OrderDetailsContext';
import { useRoutedTabs } from '../../../../widgets/tabs/components/RoutedTabs';
import FaqSection from '../common/FaqSection';
import type { RebookEligibilityType } from './rebooking/models';
import { RebookEligibility } from './rebooking/models';
import ManageTripOptionButton from './ManageTripOptionButton';
import CancellationOptions from './cancellation/CancellationOptions';
import Rebooking from './rebooking/Rebooking';
import ContactUsDrawerButton from './common/ContactUsDrawerButton';
import EditTrip from './EditTrip';
import RyanairRefundRequest from './RyanairRefundRequest';
import type {
  AllButScheduleChangeRefundOfferType,
  RefundOfferExtendedType,
  ScheduleChangeRefundOfferType,
} from './cancellation/models';

const carrierLogoStyles = css`
  width: 32px;
`;

const contentWrapperStyles = css`
  margin-bottom: 16px;
  margin-top: 16px;

  @media (min-width: ${breakpoints._768}) {
    margin-bottom: 40px;
    margin-top: 24px;
  }
`;

const requestRefundWrapperStyles = css`
  ${contentWrapperStyles};
  display: flex;
  height: 100%;
  justify-content: center;
  padding: 0 10px;
  width: 100%;
`;

const hotelsAndCarsStyles = css`
  @media (min-width: ${breakpoints._768}) {
    align-items: stretch;
    flex-direction: row;

    button {
      height: auto;
    }
  }

  @media (min-width: ${breakpoints._970}) {
    flex-direction: column;
  }
`;

const notificationStyles = css`
  margin-top: 24px;
`;

const selectionStyles = css`
  padding-bottom: 24px;
`;

const OrderDetailsManageTrip = () => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { agencyAccount, market } = useSiteContext();
  const {
    availableExtraProducts,
    cancellationOptionsData,
    cancellationOptionsError,
    cancellationOptionsLoading,
    cancellationOptionsMutation,
    isEligibleForScheduleChangeRefund,
    isUpsellAllowed,
    isWhiteLabelBrand,
    faqSectionPerSlug,
    partOfExternalFlights,
  } = useOrderDetails();
  const { tabHeading1Ref } = useRoutedTabs();
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [isRebookingSelected, setIsRebookingSelected] = useState(false);
  const [isCancelSelected, setIsCancelSelected] = useState(false);
  const [isExpirationDialogOpen, setIsExpirationDialogOpen] = useState(false);
  const isTabletAndMobileScreen = useMediaQuery(`(max-width: ${breakpoints._970})`);

  const faqSectionSlug = p('OrderDetails.ManageTrip.Faq.Slug');
  const shouldShowConsumerProtectionNotification = market.code === 'KR';
  const shouldShowHotelAndCarBanners = !agencyAccount && isUpsellAllowed && !isWhiteLabelBrand;

  const [
    rebookEligibilityMutation,
    { data: rebookingData, loading: rebookingLoading, error: rebookingError },
  ] = useMutation(REBOOK_ELIGIBILITY);

  useEffect(() => {
    rebookEligibilityMutation();
  }, [rebookEligibilityMutation]);

  useEffect(() => {
    if (!cancellationOptionsData && !cancellationOptionsLoading) {
      cancellationOptionsMutation();
    }

    if (cancellationOptionsData) {
      const { refundOffers } = cancellationOptionsData.cancellationOptions;
      if (refundOffers && refundOffers.length > 0) {
        const firstOfferExpiresInSec = differenceInSeconds(
          refundOffers[0].expirationDateTimeUTC,
          new Date().toISOString(),
        );

        if (firstOfferExpiresInSec > 0 && firstOfferExpiresInSec <= 1800) {
          timer.current = setTimeout(() => {
            setIsExpirationDialogOpen(true);
          }, firstOfferExpiresInSec * 1000);
        }
      }
    }

    return () => {
      clearTimeout(timer.current);
    };
  }, [cancellationOptionsData, cancellationOptionsLoading, cancellationOptionsMutation]);

  const cancellationOptions = cancellationOptionsData?.cancellationOptions;
  const rebookEligibilityType = rebookingData?.rebookEligibility?.rebookFlow;

  const { scheduleChangeRefundOffer, allButScheduleChangeRefundOffers } = useMemo(() => {
    if (!cancellationOptions?.refundOffers) {
      return { scheduleChangeRefundOffer: null, allButScheduleChangeRefundOffers: null };
    }
    return cancellationOptions.refundOffers.reduce(
      (
        acc: {
          scheduleChangeRefundOffer: ScheduleChangeRefundOfferType | null;
          allButScheduleChangeRefundOffers: AllButScheduleChangeRefundOfferType[];
        },
        refundOffer,
      ) => {
        const expirationTimeInSec = differenceInSeconds(
          refundOffer.expirationDateTimeUTC,
          new Date().toISOString(),
        );
        const refundOfferExtended: RefundOfferExtendedType = {
          ...refundOffer,
          expirationTimeInSec: expirationTimeInSec < 0 ? 0 : expirationTimeInSec,
        };
        if (refundOffer.refundOfferReason === CancellationRefundOfferReason.ScheduleChangeRefund) {
          acc.scheduleChangeRefundOffer = refundOfferExtended as ScheduleChangeRefundOfferType;
        } else {
          acc.allButScheduleChangeRefundOffers.push(
            refundOfferExtended as unknown as AllButScheduleChangeRefundOfferType,
          );
        }
        return acc;
      },
      { scheduleChangeRefundOffer: null, allButScheduleChangeRefundOffers: [] },
    );
  }, [cancellationOptions]);

  const allButScheduleChangeCancellationOptions = useMemo(() => {
    if (!cancellationOptions) {
      return null;
    }
    return {
      ...cancellationOptions,
      refundOffers: allButScheduleChangeRefundOffers,
    };
  }, [allButScheduleChangeRefundOffers, cancellationOptions]);

  const numberOfCancellationOptions = useMemo(() => {
    if (!allButScheduleChangeCancellationOptions) {
      return null;
    }

    const notExpiredRefundOffers =
      allButScheduleChangeCancellationOptions.refundOffers?.filter((refundOffer) =>
        isAfter(refundOffer.expirationDateTimeUTC, new Date().toISOString()),
      ) || [];
    return (
      notExpiredRefundOffers.length +
      allButScheduleChangeCancellationOptions.includedCancellationProducts.length
    );
  }, [allButScheduleChangeCancellationOptions]);

  const cancellationOptionsSubtitle = useMemo(() => {
    if (numberOfCancellationOptions === null) {
      return t('ManageTrip.Option.Unavailable.Text');
    }
    if (numberOfCancellationOptions > 0) {
      return t('ManageTrip.Cancel.NumberOfOptions.Text', numberOfCancellationOptions);
    }
    return t('ManageTrip.Cancel.NoOptions.Text');
  }, [numberOfCancellationOptions, t]);

  const rebookingSubtitle = useMemo(() => {
    switch (rebookEligibilityType) {
      case RebookEligibility.CHANGE_BY_CUSTOMER:
      case RebookEligibility.CHANGE_BY_CUSTOMER_ANCILLARIES:
      case RebookEligibility.CHANGE_BY_ETG:
      case RebookEligibility.CHANGE_BY_ETG_ANCILLARIES:
        return t('ManageTrip.Reschedule.ChangesAllowed');
      case RebookEligibility.CHANGE_FLEXIBLE_TICKET:
      case RebookEligibility.CHANGE_FLEXIBLE_TICKET_YOURSELF:
        return t('ManageTrip.Reschedule.FlexibleTicketTimeframe.Text');
      case RebookEligibility.CHANGE_BY_AIRLINE:
      case RebookEligibility.CHANGE_SHORT_DEPARTURE:
      case RebookEligibility.CHANGE_EXCHANGED_BY_AIRLINE:
      case RebookEligibility.CHANGE_NOT_AVAILABLE:
      case RebookEligibility.CHANGE_UNKNOWN:
      case RebookEligibility.CHANGE_PNR_WITH_ETG:
      case RebookEligibility.CHANGE_BY_ETG_IF_POSSIBLE:
      case RebookEligibility.CHANGE_NOT_CONFIRMED:
      case RebookEligibility.CHANGE_NOT_OPEN_TICKET:
        return t('ManageTrip.Reschedule.RescheduleOptions');
      default:
        return t('ManageTrip.Option.Unavailable.Text');
    }
  }, [rebookEligibilityType, t]);

  const handleCancelSelection = () => {
    setIsRebookingSelected(false);
    setIsCancelSelected(true);
  };

  const handleRebookingSelection = () => {
    setIsCancelSelected(false);
    setIsRebookingSelected(true);
  };

  if (isEligibleForScheduleChangeRefund) {
    return (
      <div className={requestRefundWrapperStyles}>
        {cancellationOptionsLoading ? (
          <LoadingDots />
        ) : (
          <RyanairRefundRequest scheduleChangeRefundOffer={scheduleChangeRefundOffer} />
        )}
      </div>
    );
  }

  return (
    <ContentWrapper className={contentWrapperStyles} data-testid="order-details-manage-trip">
      <h1 className={screenReadersOnlyStyles} ref={tabHeading1Ref} tabIndex={-1}>
        {t('OrderDetails.Tab.ManageTrip.Description')}
      </h1>
      <Stack spacing={{ [breakpoints._0]: 16, [breakpoints._970]: 24 }}>
        {shouldShowConsumerProtectionNotification && (
          <Notification
            className={notificationStyles}
            dataTestId="consumer-protection-law-notification"
            variant={variants.WARNING}
          >
            <Text asHtml>
              {t('OrderDetails.ManageTrip.ConsumerProtection.NoOrderChanges.Notification')}
            </Text>
          </Notification>
        )}
        <Columns
          collapseBelow={breakpoints._970}
          spacing={{ [breakpoints._0]: 16, [breakpoints._970]: 24 }}
        >
          <Column width={{ [breakpoints._0]: '12/12', [breakpoints._970]: '7/12' }}>
            <Stack spacing={{ [breakpoints._0]: 16, [breakpoints._970]: 24 }}>
              {partOfExternalFlights === PartOfExternalFlights.All && (
                <TitledCard
                  icon={
                    <CarrierLogo
                      className={carrierLogoStyles}
                      data-testid="ryanair-logo"
                      id="fr"
                      imageAlt="Ryanair"
                    />
                  }
                  title={t('OrderDetails.RyanairFlight.InfoBox.Title')}
                >
                  <RyanairFlightInfoBox />
                </TitledCard>
              )}
              <TitledCard title={t('ManageTrip.Changeability.Title')}>
                <Stack spacing={24}>
                  <Inline
                    className={cx({ [selectionStyles]: isCancelSelected || isRebookingSelected })}
                    collapseBelow={breakpoints._560}
                    noWrap
                    role="tablist"
                    spacing={16}
                  >
                    <ManageTripOptionButton
                      dataTestId="cancel-button"
                      icon={
                        <Prohibit
                          color={
                            cancellationOptionsError
                              ? vars.colors.inputs.border
                              : vars.colors.signal.critical.dark
                          }
                          size="48px"
                          weight="bold"
                        />
                      }
                      isDisabled={Boolean(cancellationOptionsError) || cancellationOptionsLoading}
                      isLoading={cancellationOptionsLoading}
                      isSelected={isCancelSelected}
                      onClick={handleCancelSelection}
                      snowplowLabel="CANCELLATION_OPTIONS"
                      subtitle={cancellationOptionsSubtitle}
                      title={t('ManageTrip.Cancel.Title')}
                    />
                    <ManageTripOptionButton
                      dataTestId="rebooking-button"
                      icon={
                        <Calendar
                          color={
                            rebookingError
                              ? vars.colors.inputs.border
                              : vars.colors.signal.positive.dark
                          }
                          size="48px"
                          weight="fill"
                        />
                      }
                      isDisabled={Boolean(rebookingError) || rebookingLoading}
                      isLoading={rebookingLoading}
                      isSelected={isRebookingSelected}
                      onClick={handleRebookingSelection}
                      snowplowLabel="RESCHEDULE"
                      subtitle={rebookingSubtitle}
                      title={t('ManageTrip.Reschedule.Title')}
                    />
                  </Inline>
                  {isRebookingSelected && rebookEligibilityType && (
                    <Rebooking
                      rebookEligibilityType={rebookEligibilityType as RebookEligibilityType}
                    />
                  )}
                  {isCancelSelected &&
                    allButScheduleChangeCancellationOptions !== null &&
                    numberOfCancellationOptions !== null &&
                    (numberOfCancellationOptions > 0 ? (
                      <CancellationOptions
                        cancellationOptions={allButScheduleChangeCancellationOptions}
                        isExpirationDialogOpen={isExpirationDialogOpen}
                        setIsExpirationDialogOpen={setIsExpirationDialogOpen}
                      />
                    ) : (
                      <Stack
                        aria-labelledby="cancel-button"
                        id="tab-panel-cancel-button"
                        role="tabpanel"
                        spacing={8}
                      >
                        <Heading as="h3" level={2}>
                          {t(
                            partOfExternalFlights === PartOfExternalFlights.All
                              ? 'ManageTrip.Cancel.RyanairFlight.NotAllowed.Title'
                              : 'ManageTrip.Cancel.NotAllowed.Title',
                          )}
                        </Heading>
                        <Text asHtml>
                          {t(
                            partOfExternalFlights === PartOfExternalFlights.All
                              ? 'ManageTrip.Cancel.RyanairFlight.NotAllowed.Description'
                              : 'ManageTrip.Cancel.NotAllowed.Description',
                          )}
                        </Text>
                      </Stack>
                    ))}
                </Stack>
              </TitledCard>
              {isCancelSelected && (
                <TitledCard
                  headingAs="h3"
                  title={t('ManageTrip.Cancel.AdditionalInformation.Title')}
                >
                  <Stack>
                    <FareRulesDrawerButton
                      snowplow={{
                        category: 'Manage Trip',
                        property: 'CANCELLATION_OPTIONS',
                        label: 'FARE_RULES',
                      }}
                    />
                    {!cancellationOptions ||
                      (numberOfCancellationOptions === 0 &&
                      partOfExternalFlights === PartOfExternalFlights.All ? null : (
                        <ContactUsDrawerButton
                          chatType="genesys"
                          snowplow={{
                            property: 'CANCELLATION_OPTIONS',
                            category: 'Manage Trip',
                            label: 'CONTACT_US',
                          }}
                        />
                      ))}
                  </Stack>
                </TitledCard>
              )}
              {partOfExternalFlights !== PartOfExternalFlights.All && <EditTrip />}
              {!isTabletAndMobileScreen && faqSectionPerSlug?.[faqSectionSlug] && (
                <FaqSection section={faqSectionPerSlug[faqSectionSlug]} shouldRedirectToContactUs />
              )}
            </Stack>
          </Column>
          <Column width={{ [breakpoints._0]: '12/12', [breakpoints._970]: '5/12' }}>
            <Stack>
              {availableExtraProducts && availableExtraProducts.length > 0 && (
                <OrderDetailsUpgradeTripBanner
                  dataTestId="manage-trip"
                  shouldDisplayInline={false}
                />
              )}
              {shouldShowHotelAndCarBanners && (
                <HotelsAndCars className={hotelsAndCarsStyles} dataTestIdPrefix="manage-trip" />
              )}
              {isTabletAndMobileScreen && faqSectionPerSlug?.[faqSectionSlug] && (
                <FaqSection section={faqSectionPerSlug[faqSectionSlug]} shouldRedirectToContactUs />
              )}
            </Stack>
          </Column>
        </Columns>
      </Stack>
    </ContentWrapper>
  );
};

export default OrderDetailsManageTrip;
